import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { initPushNotifications, isPushSupported, getPushBlocker } from '../services/pushNotifications';
import api from '../services/api';

const NotificationContext = createContext();

// Re-ask after a week rather than once per browser forever.
const PUSH_ASKED_AT_KEY = 'wl_push_asked_at';
const ASK_COOLDOWN_MS = 7 * 24 * 3600 * 1000;

export function useNotifications() {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotifications must be used within NotificationProvider');
    }
    return context;
}

function askedRecently() {
    try {
        const at = parseInt(localStorage.getItem(PUSH_ASKED_AT_KEY) || '0', 10);
        return at > 0 && Date.now() - at < ASK_COOLDOWN_MS;
    } catch { return false; }
}

export function NotificationProvider({ children }) {
    const { isAuthenticated } = useAuth();
    const [pushEnabled, setPushEnabled] = useState(false);
    const [pushSubscribed, setPushSubscribed] = useState(false);
    const [pushSupported] = useState(() => isPushSupported());
    const [pushBlocker, setPushBlocker] = useState(() => getPushBlocker());
    const [hasBeenAsked, setHasBeenAsked] = useState(askedRecently);

    // Truth comes from the server's subscription table, not the profile default.
    useEffect(() => {
        let cancelled = false;
        api.fetchPushStatus().then(res => {
            if (!cancelled && res?.success) {
                setPushEnabled(!!res.enabled);
                setPushSubscribed(!!res.subscribed);
            }
        }).catch(() => {});
        return () => { cancelled = true; };
    }, [isAuthenticated]);

    const markAsked = useCallback(() => {
        setHasBeenAsked(true);
        try { localStorage.setItem(PUSH_ASKED_AT_KEY, String(Date.now())); } catch { /* ignore */ }
    }, []);

    // surface: where the ask came from (Settings passes the click event, so anything else counts as 'settings').
    // Resolves to 'subscribed' or the failure reason, which is also the recorded outcome.
    const requestPush = useCallback(async (surface) => {
        const where = typeof surface === 'string' ? surface : 'settings';
        setHasBeenAsked(true);
        try { localStorage.setItem(PUSH_ASKED_AT_KEY, String(Date.now())); } catch { /* ignore */ }
        api.recordPushPrompt({ step: 'tap', surface: where });

        const result = await initPushNotifications();
        if (!result || result.failure) {
            setPushBlocker(getPushBlocker());
            const outcome = result?.failure || 'error';
            api.recordPushPrompt({ step: 'result', surface: where, outcome });
            return outcome;
        }

        // Auto-detect user timezone
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

        try {
            await api.registerPushSubscription({ ...result, timezone });
            setPushEnabled(true);
            setPushSubscribed(true);
            api.recordPushPrompt({ step: 'result', surface: where, outcome: 'subscribed' });
            return 'subscribed';
        } catch (err) {
            console.error('Failed to register push subscription:', err);
            api.recordPushPrompt({ step: 'result', surface: where, outcome: 'register_failed' });
            return 'register_failed';
        }
    }, []);

    const togglePush = useCallback(async (enabled) => {
        try {
            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
            await api.updatePushPreferences(enabled, timezone);
            setPushEnabled(enabled);
        } catch (err) {
            console.error('Failed to update push preferences:', err);
        }
    }, []);

    const value = {
        pushEnabled,
        pushSubscribed,
        pushSupported,
        pushBlocker,
        hasBeenAsked,
        markAsked,
        requestPush,
        togglePush,
    };

    return (
        <NotificationContext.Provider value={value}>
            {children}
        </NotificationContext.Provider>
    );
}

export default NotificationContext;
